import { ComponentPublicInstance, Directive, DirectiveBinding } from "vue"
import tippy, { Instance, Props } from "tippy.js"
import { createPaywallTippyProps, cursorReference, PaywallContext } from "@/helpers/PaywallHelper"

type PaywallBindingValue = {
  enabled: boolean
  trigger?: Props["trigger"]
  placement?: Props["placement"]
  delay?: Props["delay"]
  reference?: "element" | "cursor"
}

type PaywallElement = HTMLElement & {
  _paywallTippy?: Instance
}

type PaywallComponentInstance = ComponentPublicInstance & {
  $store: {
    getters: Record<string, unknown>
    dispatch: (action: string) => unknown
  }
  $t: (key: string) => string
}

const getContext = (binding: DirectiveBinding<PaywallBindingValue>): PaywallContext => {
  const instance = binding.instance as PaywallComponentInstance

  return {
    isAuthenticated: () => Boolean(instance.$store.getters.isAuthenticated),
    t: (key: string) => String(instance.$t(key)),
    onLogin: () => instance.$store.dispatch("toggleAuthModal"),
  }
}

const getProps = (binding: DirectiveBinding<PaywallBindingValue>): Partial<Props> => {
  const value = binding.value

  return createPaywallTippyProps(getContext(binding), {
    trigger: value.trigger || "click",
    placement: value.placement || "top-start",
    delay: value.delay || [100, 500],
    hideOnClick: true,
    onTrigger(instance, event) {
      if (value.reference === "cursor" && event instanceof MouseEvent) {
        instance.setProps({
          getReferenceClientRect: cursorReference(event),
        })
      }
    },
  })
}

const destroy = (element: PaywallElement) => {
  if (element._paywallTippy) {
    element._paywallTippy.destroy()
    delete element._paywallTippy
  }
}

const mountOrUpdate = (element: PaywallElement, binding: DirectiveBinding<PaywallBindingValue>) => {
  if (!binding.value?.enabled) {
    destroy(element)
    return
  }

  if (element._paywallTippy) {
    element._paywallTippy.setProps(getProps(binding))
    return
  }

  element._paywallTippy = tippy(element, getProps(binding))
}

const paywall: Directive<PaywallElement, PaywallBindingValue> = {
  mounted: mountOrUpdate,
  updated: mountOrUpdate,
  unmounted: destroy,
}

export default paywall
